import {
  createContext,
  useContext,
  useMemo,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type {
  BankAccount,
  BankConnection,
  BankTransaction,
  BusinessOpportunity,
  CompanyUpdate,
  FinancialItem,
  InvestmentCase,
  InvestmentCompany,
  LegalEntity,
  MonthlyUpdate,
  RealEstateProperty,
} from '../types';
import {
  emptyBankWorkspaceState,
  useBankWorkspaceState,
  type BankWorkspaceState,
} from '../hooks/useBankWorkspaceState';
import {
  emptyBusinessOpportunitiesState,
  useBusinessOpportunitiesState,
  type BusinessOpportunitiesState,
} from '../hooks/useBusinessOpportunitiesState';
import {
  emptyRealEstateWorkspaceState,
  useRealEstateWorkspaceState,
  type RealEstateWorkspaceState,
} from '../hooks/useRealEstateWorkspaceState';
import {
  emptyLegalEntitiesState,
  useLegalEntitiesState,
  type LegalEntitiesState,
} from '../hooks/useLegalEntitiesState';

export type {
  BankWorkspaceState,
  RealEstateWorkspaceState,
  BusinessOpportunitiesState,
  LegalEntitiesState,
};

export type AppDataContextValue = {
  financialItems: FinancialItem[];
  setFinancialItems: Dispatch<SetStateAction<FinancialItem[]>>;
  companies: InvestmentCompany[];
  setCompanies: Dispatch<SetStateAction<InvestmentCompany[]>>;
  investmentCases: InvestmentCase[];
  setInvestmentCases: Dispatch<SetStateAction<InvestmentCase[]>>;
  updates: CompanyUpdate[];
  setUpdates: Dispatch<SetStateAction<CompanyUpdate[]>>;
  monthlyUpdates: MonthlyUpdate[];
  setMonthlyUpdates: Dispatch<SetStateAction<MonthlyUpdate[]>>;
  businessOpportunities?: BusinessOpportunity[];
  businessOpportunitiesLoading?: boolean;
  businessOpportunitiesError?: string | null;
  bankConnections?: BankConnection[];
  bankAccounts?: BankAccount[];
  bankTransactions?: BankTransaction[];
  bankLoading?: boolean;
  bankError?: string | null;
  realEstateItems?: RealEstateProperty[];
  realEstateLoading?: boolean;
  realEstateError?: string | null;
  legalEntities?: LegalEntity[];
  legalEntitiesLoading?: boolean;
  legalEntitiesError?: string | null;
};

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);

export function AppDataProvider({
  value,
  children,
}: {
  value: AppDataContextValue;
  children: ReactNode;
}) {
  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataContextValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}

/** Merges live bank subscriptions into an existing workspace context value. */
export function BankWorkspaceBridge({
  userId,
  workspace,
  children,
}: {
  userId: string;
  workspace: Omit<
    AppDataContextValue,
    | 'bankConnections'
    | 'bankAccounts'
    | 'bankTransactions'
    | 'bankLoading'
    | 'bankError'
  >;
  children: ReactNode;
}) {
  const bank = useBankWorkspaceState(userId);
  const value = useMemo<AppDataContextValue>(
    () => ({
      ...workspace,
      bankConnections: bank.bankConnections,
      bankAccounts: bank.bankAccounts,
      bankTransactions: bank.bankTransactions,
      bankLoading: bank.loading,
      bankError: bank.error,
    }),
    [workspace, bank]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/** Merges live real-estate subscriptions into the surrounding workspace context value. */
export function RealEstateWorkspaceBridge({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const base = useAppData();
  const realEstate = useRealEstateWorkspaceState(userId);
  const value = useMemo<AppDataContextValue>(
    () => ({
      ...base,
      realEstateItems: realEstate.realEstateItems,
      realEstateLoading: realEstate.loading,
      realEstateError: realEstate.error,
    }),
    [base, realEstate]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/** Merges live legal-entity subscriptions into the surrounding workspace context value. */
export function LegalEntitiesBridge({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const base = useAppData();
  const entitiesState = useLegalEntitiesState(userId);
  const value = useMemo<AppDataContextValue>(
    () => ({
      ...base,
      legalEntities: entitiesState.legalEntities,
      legalEntitiesLoading: entitiesState.loading,
      legalEntitiesError: entitiesState.error,
    }),
    [base, entitiesState]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/** Merges live business-opportunity subscriptions into the surrounding workspace context value. */
export function BusinessOpportunitiesBridge({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const base = useAppData();
  const opportunities = useBusinessOpportunitiesState(userId);
  const value = useMemo<AppDataContextValue>(
    () => ({
      ...base,
      businessOpportunities: opportunities.businessOpportunities,
      businessOpportunitiesLoading: opportunities.loading,
      businessOpportunitiesError: opportunities.error,
    }),
    [base, opportunities]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export {
  emptyBankWorkspaceState,
  useBankWorkspaceState,
  emptyRealEstateWorkspaceState,
  useRealEstateWorkspaceState,
  emptyBusinessOpportunitiesState,
  useBusinessOpportunitiesState,
  emptyLegalEntitiesState,
  useLegalEntitiesState,
};
